import type { ErrorReporter } from '@elara/client-core/lib/error-reporting';

import * as Sentry from '@sentry/react';

import { isReportableError, setErrorReporter } from '@elara/client-core/lib/error-reporting';
import { describeNetworkFlowError } from '@elara/client-core/lib/flow-utils';

import app from '#app.json' with { type: 'json' };
import { Env } from '#env.js';
import { resolveSentryRuntimeFlags } from '#lib/sentry-config.js';
import { isSharedItineraryPath, scrubShareTokens } from '#lib/sentry-scrub.js';

export type { Client } from '@sentry/core';

const hasEnabledEnv = import.meta.env.VITE_SENTRY_ENABLED !== undefined;
const hasViteSpotlightEnv = import.meta.env.VITE_SENTRY_SPOTLIGHT !== undefined;
const hasSentrySpotlightEnv = import.meta.env.SENTRY_SPOTLIGHT !== undefined;
const runtimeFlags = resolveSentryRuntimeFlags({
  mode: Env.MODE,
  hasEnabledEnv,
  enabledFromEnv: Env.VITE_SENTRY_ENABLED,
  hasSpotlightEnv: hasViteSpotlightEnv || hasSentrySpotlightEnv,
  spotlightFromEnv: hasViteSpotlightEnv ? Env.VITE_SENTRY_SPOTLIGHT : Boolean(Env.SENTRY_SPOTLIGHT),
});

const spotlightSidecarUrl = Env.VITE_SENTRY_SPOTLIGHT_URL || Env.SENTRY_SPOTLIGHT_URL;

// A public shared itinerary's URL is a bearer credential. Session replay records page URLs and has no
// hook to rewrite them, so it is never started on a share page (always entered directly; nothing in
// the app navigates to one). Every other payload is scrubbed by the `before*` hooks below.
const isSharePage = typeof window !== 'undefined' && isSharedItineraryPath(window.location.pathname);

export const sentry = Sentry.init({
  dsn: runtimeFlags.suppressDsn ? undefined : Env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({ consistentTraceSampling: true }),
    runtimeFlags.spotlightEnabled ? Sentry.spotlightBrowserIntegration({ sidecarUrl: spotlightSidecarUrl }) : undefined,
    isSharePage
      ? undefined
      : Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false, maskAllInputs: false }),
    Sentry.consoleLoggingIntegration({ levels: ['log', 'info', 'warn', 'error'] }),
    // Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.feedbackIntegration({ colorScheme: 'system', autoInject: false }),
  ].filter((i) => !!i),
  environment: Env.MODE === 'production' ? (Env.GITHUB_REF_NAME_SLUG === 'main' ? 'production' : 'staging') : Env.MODE,
  enabled: runtimeFlags.enabled,
  enableLogs: true,
  attachStacktrace: true,
  tracesSampleRate: 1,
  tracePropagationTargets: [
    'http://localhost',
    'http://127.0.0.1',
    /^http:\/\/localhost:\d+\/api\/.*/,
    /^http:\/\/127\.0\.0\.1:\d+\/api\/.*/,
    ...app.firebase.apps.flatMap((app) => [
      new RegExp(String.raw`^https://${app}\.web\.app$`),
      new RegExp(String.raw`^https://${app}--pr(\d+)-[\w-]+\.web\.app$`),
      new RegExp(String.raw`^https://${app}--pr(\d+)-[\w-]+\.firebaseapp\.com$`),
      new RegExp(String.raw`^https://${app}\.web\.app/api/.*$`),
      new RegExp(String.raw`^https://${app}--pr(\d+)-[\w-]+\.web\.app/api/.*$`),
      new RegExp(String.raw`^https://${app}\.firebaseapp\.com/api/.*$`),
      new RegExp(String.raw`^https://${app}--pr(\d+)-[\w-]+\.firebaseapp\.com/api/.*$`),
    ]),
  ],
  propagateTraceparent: true,
  // Rewrite `/share/<token>` to `/share/:token` in everything Sentry sends: errors (request URL,
  // breadcrumbs, contexts), transactions and their spans, standalone spans, breadcrumbs and logs.
  // Genuinely-offline flow failures are expected, not defects, so they are dropped; a network-level
  // failure while online is still sent, with its raw rejection lifted into a context (ELARA-1146).
  beforeSend: (event, hint) => {
    if (!isReportableError(hint.originalException)) return null;
    const networkFailure = describeNetworkFlowError(hint.originalException);
    if (networkFailure) event.contexts = { ...event.contexts, network_failure: networkFailure };
    return scrubShareTokens(event);
  },
  beforeSendTransaction: (event) => scrubShareTokens(event),
  beforeSendSpan: (span) => scrubShareTokens(span),
  beforeBreadcrumb: (breadcrumb) => scrubShareTokens(breadcrumb),
  beforeSendLog: (log) => scrubShareTokens(log),
  // Session Replay
  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: 1,
  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  replaysOnErrorSampleRate: 1,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  ignoreSpans: [
    // Browser connection events
    { op: /^browser\.(cache|connect|DNS)$/ },
    // Fonts
    { op: 'resource.other', name: /.+\.(woff2|woff|ttf|eot)$/ },
    // CSS files
    { op: 'resource.link', name: /.+\.css.*$/ },
    // JS files
    { op: /resource\.(link|script)/, name: /.+\.js.*$/ },
    // Images
    { op: /resource\.(other|img)/, name: /.+\.(png|svg|jpe?g|gif|bmp|tiff?|webp|avif|heic?|ico).*$/ },
    // Measure spans
    { op: 'measure' },
  ],
});

Sentry.setContext('build', { branch: Env.GITHUB_REF_NAME_SLUG, commit: Env.GITHUB_SHA });

/**
 * Sentry-backed implementation of the platform-neutral `ErrorReporter` port.
 * Injected here so the shared data engine and widget render trunk can report
 * errors without importing `@sentry/react` directly.
 */
export const sentryErrorReporter: ErrorReporter = {
  captureException: (error, context) => {
    Sentry.captureException(error, context);
  },
  captureMessage: (message, context) => {
    Sentry.captureMessage(message, context);
  },
  withScope: (callback) => {
    Sentry.withScope(callback);
  },
  setUser: (user) => {
    // Sentry's "forget the user" sentinel is `null`, not `undefined`.
    Sentry.setUser(user ?? null);
  },
};

setErrorReporter(sentryErrorReporter);
